/**
 * OathPreferencesModal - Contacts & Preferences management
 * OathUI version of PreferencesModal.jsx
 * Contains 3 sections matching legacy:
 *   1. Notification Preferences (toggle switches)
 *   2. Notification Schedule (day toggles + start/end times)
 *   3. External Contacts (table + add/edit via OathExternalContactModal)
 */

import { useState, useEffect, useCallback } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  getAgencyPreferences,
  updateAgencyPreference,
  getNotificationSchedule,
  updateNotificationDay,
  updateNotificationTime,
  getAgencyContacts,
  saveAgencyContact,
  deleteAgencyContact,
} from '../../services/oathUserService';
import {
  OathModal,
  OathButton,
  OathSelect,
  OathInput,
  OathAlert,
  OathSpinner,
  OathConfirmDialog,
} from './OathUI';
import OathExternalContactModal from './OathExternalContactModal';

// Day configuration matching legacy day indices
const DAYS = [
  { key: 'monday', label: 'Monday', dayIndex: 1, activeField: 'isMonday', startField: 'mondayStartTime', endField: 'mondayEndTime', apiDay: 0 },
  { key: 'tuesday', label: 'Tuesday', dayIndex: 2, activeField: 'isTuesday', startField: 'tuesdayStartTime', endField: 'tuesdayEndTime', apiDay: 1 },
  { key: 'wednesday', label: 'Wednesday', dayIndex: 3, activeField: 'isWednesday', startField: 'wednesdayStartTime', endField: 'wednesdayEndTime', apiDay: 2 },
  { key: 'thursday', label: 'Thursday', dayIndex: 4, activeField: 'isThursday', startField: 'thursdayStartTime', endField: 'thursdayEndTime', apiDay: 3 },
  { key: 'friday', label: 'Friday', dayIndex: 5, activeField: 'isFriday', startField: 'fridayStartTime', endField: 'fridayEndTime', apiDay: 4 },
  { key: 'saturday', label: 'Saturday', dayIndex: 6, activeField: 'isSaturday', startField: 'saturdayStartTime', endField: 'saturdayEndTime', apiDay: 5 },
  { key: 'sunday', label: 'Sunday', dayIndex: 7, activeField: 'isSunday', startField: 'sundayStartTime', endField: 'sundayEndTime', apiDay: 6 },
];

// Toggle switch component
const ToggleSwitch = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
  </label>
);

// Format phone number for display
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

const OathPreferencesModal = ({ isOpen, onClose, agencies, user }) => {
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [preferences, setPreferences] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(false);

  // Contact modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  // Build agency options for OathSelect
  const agencyOptions = (agencies || []).map((a) => ({
    value: a.agencyId?.toString() || '',
    label: `${a.dbaAgencyName} (${a.agencyId})`,
  }));

  // Default to first agency
  useEffect(() => {
    if (agencies && agencies.length > 0 && !selectedAgencyId) {
      setSelectedAgencyId(agencies[0].agencyId);
    }
  }, [agencies, selectedAgencyId]);

  const selectedAgency = (agencies || []).find((a) => a.agencyId === selectedAgencyId);

  // Fetch all data when modal opens or agency changes
  const fetchData = useCallback(async () => {
    if (!selectedAgencyId || !isOpen) return;
    setLoading(true);
    setError(null);

    try {
      const [prefsResponse, scheduleResponse, contactsResponse] = await Promise.all([
        getAgencyPreferences(selectedAgencyId),
        getNotificationSchedule(selectedAgencyId),
        getAgencyContacts(selectedAgencyId),
      ]);

      if (prefsResponse.success) {
        setPreferences(prefsResponse.preferences || []);
      }
      if (scheduleResponse.success) {
        setSchedule(scheduleResponse.schedule);
      }
      if (contactsResponse.success) {
        // Sort active contacts to the top, matching legacy behavior
        const sorted = (contactsResponse.contacts || []).sort((a, b) => {
          if (a.isActive && !b.isActive) return -1;
          if (!a.isActive && b.isActive) return 1;
          return 0;
        });
        setContacts(sorted);
      }
    } catch {
      setError('Failed to load preferences data.');
    } finally {
      setLoading(false);
    }
  }, [selectedAgencyId, isOpen]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refresh]);

  // Handle preference toggle
  const handleTogglePreference = async (prefId) => {
    // Optimistic update
    const prev = [...preferences];
    setPreferences((current) =>
      current.map((p) =>
        p.transferBondPreferenceId === prefId
          ? { ...p, isPreferenceActive: !p.isPreferenceActive }
          : p
      )
    );

    const result = await updateAgencyPreference(prefId);
    if (result.success) {
      const toggled = preferences.find((p) => p.transferBondPreferenceId === prefId);
      toast.success(`Updated ${toggled?.preferenceType || 'preference'}`);
    } else {
      // Revert on failure
      setPreferences(prev);
      toast.error('Failed to update preference. Please try again.');
    }
  };

  // Handle notification day toggle
  const handleNotificationToggle = async (dayIndex, checked) => {
    if (!schedule) return;
    const result = await updateNotificationDay(schedule.notificationTimeId, dayIndex, checked);
    if (result.success) {
      setRefresh((r) => !r);
    } else {
      toast.error('Unable to update notification schedule');
      setRefresh((r) => !r);
    }
  };

  // Handle time change
  const handleSetHours = async (day) => {
    if (!schedule) return;
    const start = schedule[day.startField];
    const end = schedule[day.endField];
    const result = await updateNotificationTime(schedule.notificationTimeId, start, end, day.apiDay);
    if (result.success) {
      toast.success('Updated Hours of Operation');
    } else {
      toast.error('Unable to update notification schedule');
    }
  };

  // Handle contact add/edit
  const handleAddContact = () => {
    setSelectedContact(null);
    setContactModalOpen(true);
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setContactModalOpen(true);
  };

  const handleSubmitContact = async (contactPayload) => {
    const result = await saveAgencyContact(contactPayload);
    if (!result.success) {
      throw new Error(result.error);
    }
    setRefresh((r) => !r);
  };

  // Determine if the logged-in user is the primary contact for the selected agency
  const userFullName = user ? `${user.firstName} ${user.lastName}`.trim().toLowerCase() : '';
  const userEmail = user?.email?.toLowerCase() || '';
  const primaryContactName = selectedAgency?.primaryContact?.trim().toLowerCase() || '';
  const isPrimaryContact = primaryContactName && (
    userFullName === primaryContactName || userEmail === primaryContactName
  );

  // Check if a contact is the logged-in user (don't allow self-delete)
  const isCurrentUser = (contact) => {
    if (userEmail && contact.email) {
      return contact.email.toLowerCase() === userEmail;
    }
    if (userFullName && contact.description) {
      return contact.description.trim().toLowerCase() === userFullName;
    }
    return false;
  };

  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteContact = async () => {
    const contact = contactToDelete;
    setDeleteConfirmOpen(false);
    setContactToDelete(null);
    const result = await deleteAgencyContact(contact.agentContactId);
    if (result.success) {
      toast.success(`${contact.description} removed`);
      setRefresh((r) => !r);
    } else {
      toast.error(result.error || 'Failed to remove contact');
    }
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title="Contacts & Preferences"
      size="xl"
      footer={
        <OathButton variant="outline" onClick={onClose} className="w-full sm:w-auto sm:ml-auto">
          Close
        </OathButton>
      }
    >
      <div className="space-y-6">
        {/* Agency Selector */}
        {agencyOptions.length > 1 && (
          <OathSelect
            label="Modify Agency"
            value={selectedAgencyId?.toString() || ''}
            onChange={(val) => setSelectedAgencyId(parseInt(val))}
            options={agencyOptions}
          />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <OathSpinner size="md" />
          </div>
        ) : (
          <>
            {error && <OathAlert variant="error" message={error} />}

            {/* Section 1: Notification Preferences */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Notification Preferences
              </h3>
              <ul className="text-sm text-gray-600 dark:text-gray-400 mb-3 space-y-1 pl-4">
                <li>All notifications will be sent to the phone number(s) and email(s) listed as active in the external contacts.</li>
                <li>Preferred Counties means you will only get notifications for bonds in territories you prefer. You can set these in the posting section.</li>
              </ul>

              {preferences.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {preferences.map((pref) => (
                    <ToggleSwitch
                      key={pref.transferBondPreferenceId}
                      checked={pref.isPreferenceActive}
                      onChange={() => handleTogglePreference(pref.transferBondPreferenceId)}
                      label={pref.preferenceType}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-4">
                  <OathSpinner size="sm" />
                </div>
              )}
            </div>

            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Section 2: Notification Schedule */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Notification Schedule
              </h3>

              <ToggleSwitch
                checked={schedule?.anytime ?? false}
                onChange={() => handleNotificationToggle(0, !(schedule?.anytime ?? false))}
                label="Anytime"
              />

              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 mb-3 pl-4">
                Handles when you get notified. If &quot;Anytime&quot; is selected, your contacts will be notified 24/7. Otherwise, please provide your preferred days and hours below.
              </p>

              {schedule && !schedule.anytime && (
                <div className="space-y-3">
                  {DAYS.map((day) => (
                    <div key={day.key} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                      <ToggleSwitch
                        checked={schedule[day.activeField] ?? false}
                        onChange={() =>
                          handleNotificationToggle(day.dayIndex, !(schedule[day.activeField] ?? false))
                        }
                        label={day.label}
                      />
                      <OathInput
                        label="Start Time"
                        type="time"
                        value={schedule[day.startField] ?? ''}
                        onChange={(val) =>
                          setSchedule((prev) => ({ ...prev, [day.startField]: val }))
                        }
                      />
                      <OathInput
                        label="End Time"
                        type="time"
                        value={schedule[day.endField] ?? ''}
                        onChange={(val) =>
                          setSchedule((prev) => ({ ...prev, [day.endField]: val }))
                        }
                      />
                      <OathButton
                        size="sm"
                        onClick={() => handleSetHours(day)}
                        className="w-full"
                      >
                        Set Hours
                      </OathButton>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Section 3: External Contacts */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                External Contacts
              </h3>
              <ul className="text-sm text-gray-600 dark:text-gray-400 mb-3 space-y-1 pl-4">
                <li>Maximum of 10 contacts, only active contacts will receive notifications.</li>
                <li>You can edit any contact at any time.</li>
              </ul>

              {/* Add button - only when less than 10 contacts */}
              {contacts.length < 10 && (
                <div className="mb-3">
                  <OathButton size="sm" icon={PlusIcon} onClick={handleAddContact}>
                    Add Contact
                  </OathButton>
                </div>
              )}

              {/* Contacts table */}
              {contacts.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  No contacts yet. Click &quot;Add Contact&quot; to get started.
                </p>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                          <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Name</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Email</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Phone</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-400">Active</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-400">Can Post</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-400">Can Request</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-400">Edit</th>
                          {isPrimaryContact && <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-400"></th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {contacts.map((c) => (
                          <tr key={c.agentContactId} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="px-3 py-2 text-gray-900 dark:text-white">{c.description}</td>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{c.email || '-'}</td>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{formatPhoneNumber(c.phone) || '-'}</td>
                            <td className="px-3 py-2 text-center text-gray-700 dark:text-gray-300">{c.isActive ? 'Yes' : 'No'}</td>
                            <td className="px-3 py-2 text-center text-gray-700 dark:text-gray-300">{c.canPost ? 'Yes' : 'No'}</td>
                            <td className="px-3 py-2 text-center text-gray-700 dark:text-gray-300">{c.canRequest ? 'Yes' : 'No'}</td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => handleEditContact(c)}
                                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </td>
                            {isPrimaryContact && (
                              <td className="px-3 py-2 text-center">
                                {!isCurrentUser(c) ? (
                                  <button
                                    onClick={() => handleDeleteContact(c)}
                                    className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                    title="Remove contact"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <span className="text-[10px] text-gray-400 dark:text-gray-500">You</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden space-y-3">
                    {contacts.map((c) => (
                      <div
                        key={c.agentContactId}
                        className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 bg-white dark:bg-gray-800"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {c.description}
                            {isPrimaryContact && isCurrentUser(c) && (
                              <span className="ml-1.5 text-[10px] text-gray-400 dark:text-gray-500">(You)</span>
                            )}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditContact(c)}
                              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            {isPrimaryContact && !isCurrentUser(c) && (
                              <button
                                onClick={() => handleDeleteContact(c)}
                                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                title="Remove contact"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        {c.email && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                            <span className="font-medium">Email:</span> {c.email}
                          </p>
                        )}
                        {c.phone && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                            <span className="font-medium">Phone:</span> {formatPhoneNumber(c.phone)}
                          </p>
                        )}
                        <div className="flex gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>{c.isActive ? 'Active' : 'Inactive'}</span>
                          {c.canPost && <span>Can Post</span>}
                          {c.canRequest && <span>Can Request</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* External Contact Modal for Add/Edit */}
        <OathExternalContactModal
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          contact={selectedContact}
          onSubmit={handleSubmitContact}
          agencyId={selectedAgencyId}
        />

        {/* Delete Contact Confirmation */}
        <OathConfirmDialog
          open={deleteConfirmOpen}
          title="Remove Contact"
          message={contactToDelete ? `Are you sure you want to remove ${contactToDelete.description}?` : ''}
          confirmLabel="Remove"
          variant="danger"
          onConfirm={confirmDeleteContact}
          onCancel={() => { setDeleteConfirmOpen(false); setContactToDelete(null); }}
        />
      </div>
    </OathModal>
  );
};

export default OathPreferencesModal;
